import React, { useState, useMemo } from 'react'
import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  VStack,
  HStack,
  useDisclosure,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Switch,
  Tooltip,
  SimpleGrid,
  useToast,
  Icon,
} from '@chakra-ui/react'
import Card from 'components/card/Card'
import { SkeletonTable } from 'components/skeleton/Skeleton'
import { AiOutlineDelete, AiOutlineEdit, AiOutlinePlus, AiOutlineSearch } from 'react-icons/ai'
import { FaStripe, FaPaypal } from 'react-icons/fa'
import { MdCheckCircle, MdFlashOn, MdStarBorder, MdInfoOutline, MdAccountBalanceWallet } from 'react-icons/md'
import {
  useGetPaymentGateways,
  useToggleActiveGateway,
  useSetDefaultGateway,
  useTestGatewayConnection,
} from 'queries/paymentGateway.query'
import { GatewayModal } from './GatewayModal'
import { DeleteGatewayModal } from './DeleteGatewayModal'

export default function PaymentGatewayTable() {
  const [search, setSearch] = useState('')
  const [selectedGateway, setSelectedGateway] = useState(null)
  const [testingId, setTestingId] = useState(null)

  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'secondaryGray.400')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.50', 'gray.700')
  const cardBg = useColorModeValue('white', 'navy.800')

  const toast = useToast()
  const { isOpen: isModalOpen, onOpen: onOpenModal, onClose: onCloseModal } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure()

  const { data: gatewaysRes, isLoading } = useGetPaymentGateways()
  const toggleActiveMutation = useToggleActiveGateway()
  const setDefaultMutation = useSetDefaultGateway()
  const testMutation = useTestGatewayConnection()

  const gateways = useMemo(() => {
    if (!gatewaysRes?.data) return []
    return gatewaysRes.data
  }, [gatewaysRes])

  const filteredGateways = useMemo(() => {
    if (!search.trim()) return gateways
    const q = search.toLowerCase()
    return gateways.filter(
      (g) =>
        g.name.toLowerCase().includes(q) ||
        g.provider.toLowerCase().includes(q) ||
        (g.description && g.description.toLowerCase().includes(q))
    )
  }, [gateways, search])

  const stripeGateways = useMemo(() => gateways.filter((g) => g.provider === 'stripe'), [gateways])
  const paypalGateways = useMemo(() => gateways.filter((g) => g.provider === 'paypal'), [gateways])

  const defaultStripe = useMemo(() => stripeGateways.find((g) => g.is_default && g.is_active), [stripeGateways])
  const defaultPayPal = useMemo(() => paypalGateways.find((g) => g.is_default && g.is_active), [paypalGateways])

  const handleOpenAdd = () => {
    setSelectedGateway(null)
    onOpenModal()
  }

  const handleOpenEdit = (gateway) => {
    setSelectedGateway(gateway)
    onOpenModal()
  }

  const handleOpenDelete = (gateway) => {
    setSelectedGateway(gateway)
    onOpenDelete()
  }

  const handleToggleActive = async (gateway) => {
    try {
      await toggleActiveMutation.mutateAsync(gateway.id)
      toast({
        title: gateway.is_active ? 'Gateway Deactivated' : 'Gateway Activated',
        status: 'info',
        duration: 2500,
      })
    } catch (err) {
      toast({
        title: 'Error updating gateway status',
        description: err.response?.data?.message || err.message,
        status: 'error',
        duration: 3500,
      })
    }
  }

  const handleSetDefault = async (gateway) => {
    try {
      await setDefaultMutation.mutateAsync(gateway.id)
      toast({
        title: 'Default Gateway Set',
        description: `"${gateway.name}" is now the primary ${gateway.provider} gateway.`,
        status: 'success',
        duration: 3000,
      })
    } catch (err) {
      toast({
        title: 'Failed to set default',
        description: err.response?.data?.message || err.message,
        status: 'error',
        duration: 3500,
      })
    }
  }

  const handleTestConnection = async (gateway) => {
    setTestingId(gateway.id)
    try {
      const res = await testMutation.mutateAsync({ id: gateway.id })
      if (res?.success) {
        toast({
          title: 'Connection Verified',
          description: res.message || 'Gateway connected successfully.',
          status: 'success',
          duration: 4000,
        })
      } else {
        toast({
          title: 'Verification Failed',
          description: res?.message || 'Could not verify connection.',
          status: 'error',
          duration: 5000,
        })
      }
    } catch (err) {
      toast({
        title: 'Connection Test Failed',
        description: err.response?.data?.message || err.message,
        status: 'error',
        duration: 5000,
      })
    } finally {
      setTestingId(null)
    }
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Top Overview Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={5}>
        <Card p={5} bg={cardBg}>
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" fontWeight="bold" color={textColorSecondary}>
              Active Stripe Gateway
            </Text>
            <Box color="#635BFF" fontSize="28px">
              <FaStripe />
            </Box>
          </HStack>
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            {defaultStripe ? defaultStripe.name : 'System Default (.env)'}
          </Text>
          <HStack mt={2}>
            <Badge colorScheme={defaultStripe?.mode === 'live' ? 'green' : 'orange'} borderRadius="full" px={2}>
              {defaultStripe ? defaultStripe.mode.toUpperCase() : 'ENV DEFAULT'}
            </Badge>
            <Badge colorScheme="purple" borderRadius="full" px={2}>
              {stripeGateways.length} Configured
            </Badge>
          </HStack>
        </Card>

        <Card p={5} bg={cardBg}>
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" fontWeight="bold" color={textColorSecondary}>
              Active PayPal Gateway
            </Text>
            <Box color="#003087" fontSize="22px">
              <FaPaypal />
            </Box>
          </HStack>
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            {defaultPayPal ? defaultPayPal.name : 'System Default (.env)'}
          </Text>
          <HStack mt={2}>
            <Badge colorScheme={defaultPayPal?.mode === 'live' ? 'green' : 'orange'} borderRadius="full" px={2}>
              {defaultPayPal ? defaultPayPal.mode.toUpperCase() : 'ENV DEFAULT'}
            </Badge>
            <Badge colorScheme="blue" borderRadius="full" px={2}>
              {paypalGateways.length} Configured
            </Badge>
          </HStack>
        </Card>

        <Card p={5} bg={cardBg}>
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" fontWeight="bold" color={textColorSecondary}>
              Payment Mode
            </Text>
            <Box color="teal.500" fontSize="24px">
              <MdAccountBalanceWallet />
            </Box>
          </HStack>
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            Dynamic Switching
          </Text>
          <Text fontSize="xs" color={textColorSecondary} mt={1}>
            Switch Stripe/PayPal anytime per event or globally without restarting servers.
          </Text>
        </Card>
      </SimpleGrid>

      {/* Main Table Card */}
      <Card p={6}>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          mb={6}
          gap={4}
        >
          <VStack align="flex-start" spacing={1}>
            <Text fontSize="22px" fontWeight="700" color={textColor}>
              Payment Gateways
            </Text>
            <Text fontSize="sm" color={textColorSecondary}>
              Manage multiple Stripe and PayPal credentials. Choose defaults or assign specific accounts to events.
            </Text>
          </VStack>

          <HStack spacing={3} width={{ base: '100%', md: 'auto' }}>
            <InputGroup width={{ base: '100%', md: '260px' }}>
              <InputLeftElement pointerEvents="none">
                <AiOutlineSearch color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search gateways..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                borderRadius="14px"
              />
            </InputGroup>

            <Button
              leftIcon={<AiOutlinePlus />}
              colorScheme="brand"
              borderRadius="14px"
              px={5}
              onClick={handleOpenAdd}
            >
              Add Gateway
            </Button>
          </HStack>
        </Flex>

        {isLoading ? (
          <SkeletonTable />
        ) : filteredGateways.length === 0 ? (
          <Box py={10} textAlign="center">
            <Icon as={MdInfoOutline} boxSize="40px" color="gray.400" mb={3} />
            <Text fontSize="md" fontWeight="bold" color={textColor}>
              No dynamic payment gateways configured yet.
            </Text>
            <Text fontSize="sm" color={textColorSecondary} mt={1} maxW="500px" mx="auto">
              Checkout is currently falling back to standard .env credentials. Click "Add Gateway" to add customized Stripe or PayPal accounts!
            </Text>
            <Button mt={4} colorScheme="brand" size="sm" onClick={handleOpenAdd} borderRadius="10px">
              Add First Gateway
            </Button>
          </Box>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" color="gray.500" mb="24px">
              <Thead bg={TheadBackground}>
                <Tr my=".8rem" pl="0px" color="gray.400">
                  <Th borderColor={borderColor} color="gray.400">
                    Gateway Name
                  </Th>
                  <Th borderColor={borderColor} color="gray.400">
                    Provider
                  </Th>
                  <Th borderColor={borderColor} color="gray.400">
                    Mode
                  </Th>
                  <Th borderColor={borderColor} color="gray.400">
                    Default
                  </Th>
                  <Th borderColor={borderColor} color="gray.400">
                    Active
                  </Th>
                  <Th borderColor={borderColor} color="gray.400">
                    Key / Client ID Preview
                  </Th>
                  <Th borderColor={borderColor} color="gray.400" textAlign="right">
                    Actions
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredGateways.map((gw) => {
                  const masked = gw.masked_credentials || {}
                  const previewKey =
                    gw.provider === 'stripe'
                      ? masked.publishable_key || masked.secret_key
                      : masked.client_id

                  return (
                    <Tr
                      key={gw.id}
                      _hover={{ bg: hoverBackgroundColor }}
                      transition="background-color 0.2s"
                    >
                      {/* Name */}
                      <Td borderColor={borderColor}>
                        <VStack align="flex-start" spacing={0}>
                          <Text fontWeight="bold" color={textColor} fontSize="sm">
                            {gw.name}
                          </Text>
                          {gw.description && (
                            <Text fontSize="xs" color={textColorSecondary} maxW="280px" isTruncated>
                              {gw.description}
                            </Text>
                          )}
                        </VStack>
                      </Td>

                      {/* Provider */}
                      <Td borderColor={borderColor}>
                        <HStack spacing={2}>
                          {gw.provider === 'stripe' ? (
                            <Box color="#635BFF" fontSize="22px">
                              <FaStripe />
                            </Box>
                          ) : (
                            <Box color="#003087" fontSize="18px">
                              <FaPaypal />
                            </Box>
                          )}
                          <Text fontWeight="semibold" fontSize="sm" textTransform="capitalize">
                            {gw.provider}
                          </Text>
                        </HStack>
                      </Td>

                      {/* Mode */}
                      <Td borderColor={borderColor}>
                        <Badge
                          colorScheme={gw.mode === 'live' ? 'green' : 'orange'}
                          borderRadius="full"
                          px={2}
                          py={0.5}
                        >
                          {gw.mode.toUpperCase()}
                        </Badge>
                      </Td>

                      {/* Default */}
                      <Td borderColor={borderColor}>
                        {gw.is_default ? (
                          <Badge colorScheme="brand" borderRadius="full" px={2} py={0.5} display="inline-flex" alignItems="center" gap={1}>
                            <MdCheckCircle /> Primary Default
                          </Badge>
                        ) : (
                          <Button
                            size="xs"
                            variant="ghost"
                            leftIcon={<MdStarBorder />}
                            onClick={() => handleSetDefault(gw)}
                            borderRadius="8px"
                          >
                            Set Default
                          </Button>
                        )}
                      </Td>

                      {/* Active Toggle */}
                      <Td borderColor={borderColor}>
                        <Switch
                          colorScheme="green"
                          isChecked={gw.is_active}
                          onChange={() => handleToggleActive(gw)}
                        />
                      </Td>

                      {/* Key Preview */}
                      <Td borderColor={borderColor}>
                        <Text fontSize="xs" fontFamily="mono" color={textColorSecondary}>
                          {previewKey || '••••••••'}
                        </Text>
                      </Td>

                      {/* Actions */}
                      <Td borderColor={borderColor} textAlign="right">
                        <HStack spacing={2} justify="flex-end">
                          <Tooltip label="Test API Connection">
                            <IconButton
                              size="sm"
                              variant="outline"
                              colorScheme="orange"
                              icon={<MdFlashOn />}
                              isLoading={testingId === gw.id}
                              onClick={() => handleTestConnection(gw)}
                              borderRadius="8px"
                            />
                          </Tooltip>

                          <Tooltip label="Edit Gateway">
                            <IconButton
                              size="sm"
                              variant="outline"
                              icon={<AiOutlineEdit />}
                              onClick={() => handleOpenEdit(gw)}
                              borderRadius="8px"
                            />
                          </Tooltip>

                          <Tooltip label="Delete Gateway">
                            <IconButton
                              size="sm"
                              variant="outline"
                              colorScheme="red"
                              icon={<AiOutlineDelete />}
                              onClick={() => handleOpenDelete(gw)}
                              borderRadius="8px"
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </Box>
        )}
      </Card>

      {/* Modals */}
      <GatewayModal
        isOpen={isModalOpen}
        onClose={onCloseModal}
        gateway={selectedGateway}
      />

      <DeleteGatewayModal
        isOpen={isDeleteOpen}
        onClose={onCloseDelete}
        gateway={selectedGateway}
      />
    </VStack>
  )
}
